import { Auth } from 'aws-amplify';
import config from '../config';

// CPI-1310: shared durable preference, read on load only. Never subscribe to
// focus/storage events: already-open presentation tabs keep their language.
const languages = ['en', 'vi', 'id'];
let revision = 0;
let writes = Promise.resolve();
let unresolvedWrite = null;
const saveStatusListeners = new Set();
export function subscribeLanguageSaveStatus(listener) {
  saveStatusListeners.add(listener);
  listener(!!unresolvedWrite);
  return () => saveStatusListeners.delete(listener);
}
function notifySaveStatus() {
  saveStatusListeners.forEach((listener) => listener(!!unresolvedWrite));
}

export function normalizeLanguage(value) {
  if (typeof value !== 'string') return null;
  const code = value.toLowerCase().split(/[-_]/)[0];
  return languages.indexOf(code) >= 0 ? code : null;
}

export function siteplanLanguageURL(base, projectID, displayLanguage) {
  const query = new URLSearchParams({ projectID, source: 'lead', lang: normalizeLanguage(displayLanguage) || 'en' });
  return `${String(base || '').replace(/\/$/, '')}/preview/?${query.toString()}`;
}

async function request(method, language, selectedToken) {
  const token = selectedToken || (await Auth.currentSession()).getIdToken().getJwtToken();
  const response = await fetch(`${config.app.mhubApiUrl}/authenticated/language/`, {
    method,
    credentials: 'include',
    cache: 'no-store',
    headers: {
      'Content-Type': 'application/json',
      'X-Aws-Id-Token': token,
    },
    ...(method === 'PUT' ? { body: JSON.stringify({ currentLanguageCode: language }) } : {}),
  });
  if (!response.ok) throw new Error(`Language preference ${method} failed (${response.status})`);
  return response.json();
}

export async function readLanguagePreference() {
  const started = revision;
  await writes;
  if (unresolvedWrite) throw Object.assign(new Error('Previous language save is still awaiting a response'), { code: 'LANGUAGE_SAVE_UNCONFIRMED', blocked: true });
  if (started !== revision) return null;
  const result = await request('GET');
  // A slow initial read must never undo a selection made while it was in flight.
  return started === revision ? normalizeLanguage(result.currentLanguageCode) : null;
}

export function saveLanguagePreference(value) {
  const language = normalizeLanguage(value);
  if (!language) return Promise.reject(new Error('Unsupported language'));
  revision += 1;
  // Keep rapid selections in order, including after an earlier failed save.
  // Capture the caller at selection time. A queued old selection must not use
  // another account's token after logout/login in this browser.
  const identity = Auth.currentSession().then(
    (session) => ({ token: session.getIdToken().getJwtToken() }),
    (error) => ({ error }),
  );
  const pending = writes.then(async () => {
    if (unresolvedWrite) throw Object.assign(new Error('Previous language save is still awaiting a response'), { code: 'LANGUAGE_SAVE_UNCONFIRMED', blocked: true });
    let expired = false;
    let requestStarted = false;
    let timer;
    const timeoutError = new Error('Language preference save timed out');
    const operation = identity.then((caller) => {
      if (expired) throw timeoutError; // Never send a timed-out identity lookup's late result.
      if (caller.error) throw caller.error;
      requestStarted = true;
      return request('PUT', language, caller.token);
    });
    const settled = () => {
      if (unresolvedWrite === operation) {
        unresolvedWrite = null;
        notifySaveStatus();
      }
    };
    operation.then(settled, settled);
    try {
      return await Promise.race([
        operation,
        new Promise((resolve, reject) => {
          timer = setTimeout(() => {
            expired = true;
            // Aborting fetch cannot cancel server-side processing. Report failure,
            // but refuse overlapping writes until this actual operation settles.
            if (requestStarted) {
              unresolvedWrite = operation;
              timeoutError.code = 'LANGUAGE_SAVE_UNCONFIRMED';
              notifySaveStatus();
            }
            reject(timeoutError);
          }, 5000);
        }),
      ]);
    } finally {
      clearTimeout(timer);
    }
  });
  writes = pending.catch(() => {});
  return pending;
}
